// src/lib/download.js
// Download a file from an authenticated API endpoint.
//
// WHY THIS EXISTS RATHER THAN AN <a href="/api/data/export?entity=accounts">
// Identical to the reason startOAuthConnect() exists in src/lib/api.js. A link is
// a top-level browser navigation: it carries cookies and nothing else. Our
// Supabase session lives in localStorage (supabaseClient.js, storageKey
// 'digitalme_auth'), so the request would arrive with no Authorization header,
// getRequestContext would find no tenant, and the endpoint would answer 401 — to
// a signed-in user, every time. The link could never have worked.
//
// So: fetch it authenticated, turn the response into a blob, and click a
// synthetic anchor at an object URL.

import { apiFetch } from './api';

/**
 * Fetch `path` with the user's JWT and save the response as a file.
 *
 * @param {string} path         API path, e.g. /api/data/export?entity=accounts
 * @param {string} fallbackName Filename to use if the server sends no Content-Disposition
 * @returns {Promise<{ok: boolean, error?: string, filename?: string}>}
 */
export async function downloadFile(path, fallbackName = 'download.csv') {
  let res;
  try {
    res = await apiFetch(path);
  } catch (err) {
    return { ok: false, error: err.message || 'The download could not be started.' };
  }

  if (!res.ok) {
    // The endpoint answers JSON on failure and CSV on success, so an error body
    // is readable — and far more useful than "HTTP 400".
    let message = `The download failed (HTTP ${res.status}).`;
    try {
      const body = await res.json();
      if (body?.error) message = body.error;
    } catch {
      /* not JSON; keep the status message */
    }
    return { ok: false, error: message };
  }

  const filename = filenameFrom(res.headers.get('Content-Disposition')) || fallbackName;
  const blob = await res.blob();
  saveBlob(blob, filename);
  return { ok: true, filename };
}

/** Save a Blob under `filename`, via a temporary object URL. */
export function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  // Firefox requires the anchor to be in the document for a synthetic click to
  // take effect; Chrome does not. Appending is harmless in both.
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoking immediately can cancel the download in some browsers, so give the
  // click a turn of the event loop first.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// Content-Disposition: attachment; filename="accounts.csv"
function filenameFrom(header) {
  if (!header) return null;
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
  return match ? decodeURIComponent(match[1].trim()) : null;
}
